const LADO_MAXIMO = 2048;
const QUALIDADE = 0.88;

/**
 * Redimensiona a foto antes de enviar.
 *
 * Celular moderno produz JPEG de 10 MB ou mais. Numa rede de evento isso é a
 * diferença entre dois segundos e um minuto de espera com uma pessoa parada na
 * frente do operador — e o modelo de geração não usa nada além de 2048px.
 *
 * Se qualquer etapa falhar, devolve o arquivo original: enviar grande é ruim,
 * não enviar é pior.
 */
export async function reduzirImagem(arquivo: File): Promise<File> {
  if (!arquivo.type.startsWith("image/")) return arquivo;

  try {
    const bitmap = await createImageBitmap(arquivo);
    const maior = Math.max(bitmap.width, bitmap.height);

    if (maior <= LADO_MAXIMO) {
      bitmap.close();
      return arquivo;
    }

    const escala = LADO_MAXIMO / maior;
    const largura = Math.round(bitmap.width * escala);
    const altura = Math.round(bitmap.height * escala);

    const canvas = document.createElement("canvas");
    canvas.width = largura;
    canvas.height = altura;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return arquivo;
    }

    ctx.drawImage(bitmap, 0, 0, largura, altura);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", QUALIDADE),
    );

    if (!blob || blob.size >= arquivo.size) return arquivo;

    return new File([blob], trocarExtensao(arquivo.name, "jpg"), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return arquivo;
  }
}

function trocarExtensao(nome: string, nova: string): string {
  return `${nome.replace(/\.[^.]+$/, "")}.${nova}`;
}

export function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
